/**
 * P0-A: Agent 可观测性面板 — trace 列表 + span 时间线
 * 轮询 /api/observability/traces + 监听 agent:trace socket 事件实时刷新
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { apiFetch } from '../services/apiClient';
import { Activity, AlertTriangle, CheckCircle2, Clock, Cpu, Wrench, Brain, Database, Shield, RefreshCw } from 'lucide-react';
import { useSocket } from '../hooks/useSocket';

interface TraceSpan {
  id: string;
  traceId: string;
  name: string;
  type: 'turn' | 'cognition' | 'memory' | 'llm' | 'tool' | 'guardrail' | 'tts' | 'stt';
  status: 'running' | 'ok' | 'error' | 'timeout' | 'cancelled';
  startTime: number;
  endTime?: number;
  durationMs?: number;
  metadata?: Record<string, unknown>;
  error?: string;
}

interface TraceSummary {
  id: string;
  userId: string;
  agentId: string;
  input: string;
  output?: string;
  status: string;
  startTime: number;
  endTime?: number;
  totalDurationMs?: number;
  totalTokens?: number;
  spanCount: number;
}

const SPAN_STYLE: Record<string, { color: string; icon: any }> = {
  llm: { color: 'text-sky-300 border-sky-400/30 bg-sky-400/10', icon: Cpu },
  tool: { color: 'text-orange-300 border-orange-400/30 bg-orange-400/10', icon: Wrench },
  cognition: { color: 'text-violet-300 border-violet-400/30 bg-violet-400/10', icon: Brain },
  memory: { color: 'text-emerald-300 border-emerald-400/30 bg-emerald-400/10', icon: Database },
  guardrail: { color: 'text-amber-300 border-amber-400/30 bg-amber-400/10', icon: Shield },
  turn: { color: 'text-cyan-300 border-cyan-400/30 bg-cyan-400/10', icon: Activity },
};

const spanStyle = (type: string) => SPAN_STYLE[type] || SPAN_STYLE.turn;

const formatMs = (ms?: number) => {
  if (ms == null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const formatTime = (ts: number) => {
  try {
    return new Date(ts).toLocaleTimeString('zh-CN', { hour12: false });
  } catch {
    return '-';
  }
};

export function AgentTracePanel({ t }: { t: any }) {
  const socket = useSocket();
  const [traces, setTraces] = useState<TraceSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [detail, setDetail] = useState<{ spans: TraceSpan[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadTraces = useCallback(async () => {
    setError('');
    try {
      const res = await apiFetch('/api/observability/traces?limit=30', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed to load traces (${res.status})`);
      setTraces(data.traces || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    try {
      const res = await apiFetch(`/api/observability/traces/${id}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setDetail(data);
    } catch { /* 详情加载失败不阻断列表 */ }
  }, []);

  useEffect(() => {
    void loadTraces();
    const timer = window.setInterval(() => void loadTraces(), 5000);
    return () => window.clearInterval(timer);
  }, [loadTraces]);

  // 实时刷新：收到 agent:trace 事件时重新拉取
  useEffect(() => {
    if (!socket) return;
    const onTrace = () => {
      void loadTraces();
      if (selectedId) void loadDetail(selectedId);
    };
    socket.on('agent:trace', onTrace);
    return () => { socket.off('agent:trace', onTrace); };
  }, [socket, loadTraces, loadDetail, selectedId]);

  const selected = useMemo(() => traces.find(tr => tr.id === selectedId) || traces[0], [selectedId, traces]);

  useEffect(() => {
    if (selected) void loadDetail(selected.id);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const spans = detail?.spans || [];

  return (
    <div className="grid h-full min-h-0 grid-cols-[280px_1fr]">
      {/* 左侧 trace 列表 */}
      <aside className="min-h-0 overflow-y-auto border-r border-white/10 bg-black/20 p-3 custom-scrollbar">
        {traces.length === 0 && (
          <div className="rounded-lg border border-dashed border-white/10 px-3 py-8 text-center text-xs text-white/35">
            {loading ? '加载追踪中...' : '暂无 Agent 追踪记录，发一条消息试试'}
          </div>
        )}
        <div className="space-y-2">
          {traces.map(tr => {
            const active = tr.id === selected?.id;
            const isError = tr.status === 'error' || tr.status === 'timeout';
            return (
              <button
                type="button"
                key={tr.id}
                onClick={() => setSelectedId(tr.id)}
                className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${active ? 'border-cyan-400/30 bg-cyan-400/10' : 'border-white/8 bg-white/[0.03] hover:bg-white/[0.06]'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${isError ? 'text-red-300' : 'text-emerald-300'}`}>
                    {isError ? <AlertTriangle size={11} /> : <CheckCircle2 size={11} />}
                    {tr.status}
                  </span>
                  <span className="text-[10px] text-white/30">{formatTime(tr.startTime)}</span>
                </div>
                <div className="mt-1 truncate text-xs text-white/75">{tr.input || '(空输入)'}</div>
                <div className="mt-1 flex items-center gap-2 text-[10px] text-white/35">
                  <span className="inline-flex items-center gap-1"><Clock size={10} />{formatMs(tr.totalDurationMs)}</span>
                  {tr.totalTokens != null && <span>{tr.totalTokens} tok</span>}
                  <span>{tr.spanCount} spans</span>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* 右侧 span 时间线 */}
      <section className="min-h-0 overflow-y-auto p-4 custom-scrollbar">
        {error && <div className="mb-3 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>}
        {selected && (
          <div className="mb-4 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
            <div className="text-xs font-bold uppercase tracking-widest text-cyan-300">Trace 详情</div>
            <div className="mt-2 text-sm text-white/80">输入：{selected.input}</div>
            {selected.output && <div className="mt-1 text-sm text-white/60">输出：{selected.output}</div>}
          </div>
        )}
        {spans.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-white/35">
            {selected ? '此追踪暂无 span 数据' : '选择左侧追踪记录查看执行时间线'}
          </div>
        ) : (
          <div className="space-y-2">
            {spans.map(span => {
              const style = spanStyle(span.type);
              const Icon = style.icon;
              const isErr = span.status === 'error' || span.status === 'timeout';
              return (
                <div key={span.id} className={`rounded-lg border px-3 py-2 ${isErr ? 'border-red-500/30 bg-red-500/10' : 'border-white/8 bg-white/[0.03]'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${isErr ? 'text-red-300' : style.color.split(' ')[0]}`}>
                      <Icon size={13} />
                      {span.name}
                    </span>
                    <span className="flex items-center gap-2 text-[10px] text-white/35">
                      <span className={`rounded px-1.5 py-0.5 ${style.color}`}>{span.type}</span>
                      <span>{formatMs(span.durationMs)}</span>
                    </span>
                  </div>
                  {span.metadata && Object.keys(span.metadata).length > 0 && (
                    <div className="mt-1 text-[10px] text-white/40">
                      {Object.entries(span.metadata).map(([k, v]) => `${k}=${String(v)}`).join(' · ')}
                    </div>
                  )}
                  {span.error && <div className="mt-1 text-[11px] text-red-300">{span.error}</div>}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
